import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, Text, Button } from '@chakra-ui/react';
import Navbar from '../components/Navbar.js';
import FooterSmall from '../components/FooterSmall.js';
import AuthService from '../services/auth-service';
import { Flex } from '@chakra-ui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import BeatLoader from 'react-spinners/BeatLoader';

import { Mixpanel } from '../utils/Mixpanel';
import { AiOutlineEyeInvisible, AiOutlineEye } from 'react-icons/ai';

export default function Register() {
  const [showPassword, setShowPassword] = useState(false);

  Mixpanel.track('Viewed Sign Up Page');
  const navigate = useNavigate();
  const toast = useToast();
  const SignUpSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, 'Too short!')
      .max(50, 'Too long!')
      .required('Required'),
    lastName: Yup.string()
      .min(2, 'Too short!')
      .max(50, 'Too long!')
      .required('Required'),
    username: Yup.string()
      .min(2, 'Too short!')
      .max(50, 'Too long!')
      .required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    password: Yup.string().min(8, 'Too short!').required('Required'),
  });

  return (
    <>
      <Flex
        w="100%"
        h="100%"
        flexDirection="column"
        bg="#1a202c"
        minHeight="100%"
        position="static"
        overflow="auto"
        backgroundColor="white"
        backgroundSize="100% auto"
        backgroundRepeat="repeat"
        backgroundPosition="center"
      >
        <Navbar transparent={false} />
        <Flex
          minHeight="calc(100vh - 60px)"
          w="auto"
          justifyContent="center"
          alignContent="center"
        >
          <Flex
            m="30px"
            mt="100px"
            rounded="5px"
            w={['100%', '80%', '60%', '40%']}
            alignItems="center"
            justifyContent="center"
          >
            <Flex w={['100%', '80%', '60%', '80%']} flexDirection="column">
              <div className="w-full container mx-auto h-auto">
                <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-lg bg-white border-2">
                  <div className="rounded-t mb-0 px-6 py-6">
                    <div className="text-center mb-3">
                      <h6 className="text-gray-700 text-sm uppercase font-bold">
                        Create Account
                      </h6>
                    </div>
                  </div>
                  <div className=" flex-auto px-4 lg:px-10 mb-20 pt-0">
                    <Formik
                      initialValues={{
                        email: '',
                        password: '',
                        firstName: '',
                        lastName: '',
                        username: '',
                      }}
                      validationSchema={SignUpSchema}
                      onSubmit={async (
                        values,
                        { setSubmitting, setErrors }
                      ) => {
                        setSubmitting(true);
                        try {
                          const newUser = {
                            user: {
                              email: values.email,
                              password: values.password,
                              firstname: values.firstName,
                              lastname: values.lastName,
                              username: values.username,
                              career_goal: '',
                            },
                          };
                          await AuthService.register(newUser);
                          const currentUser =
                            await AuthService.fetchCurrentUser();
                          toast({
                            title: 'Welcome to Techstarta! 🎉',
                            description: "We've created your account for you.",
                            status: 'success',
                            duration: 3000,
                            isClosable: true,
                          });
                          Mixpanel.identify(currentUser.id);
                          Mixpanel.track('Account Created', {
                            email: values.email,
                            firstName: values.firstName,
                          });
                          navigate('/verification_required');
                        } catch (error) {
                          Mixpanel.track('Failed registration');
                          if (error.status === 400 || 403) {
                            setErrors(error.data.errors);
                          }
                        }
                        setSubmitting(false);
                      }}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        isValid,
                        dirty,
                      }) => (
                        <form onSubmit={handleSubmit}>
                          <div className="relative w-full mb-4">
                            <label
                              className="block uppercase text-gray-700 text-xs font-bold mb-2"
                              htmlFor="firstName"
                            >
                              First Name
                            </label>
                            <input
                              id="firstName"
                              type="text"
                              name="firstName"
                              className="px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm border focus:outline-none focus:shadow-outline w-full"
                              placeholder="First name"
                              value={values.firstName}
                              style={{ transition: 'all .15s ease' }}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                            <Text
                              borderRadius="md"
                              fontSize="sm"
                              mt="1"
                              textColor="red.500"
                            >
                              {errors.firstName && touched.firstName
                                ? errors.firstName
                                : null}
                            </Text>
                          </div>
                          <div className="relative w-full mb-4">
                            <label
                              className="block uppercase text-gray-700 text-xs font-bold mb-2"
                              htmlFor="lastName"
                            >
                              Last Name
                            </label>
                            <input
                              id="lastName"
                              type="text"
                              value={values.lastName}
                              className="px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm border focus:outline-none focus:shadow-outline w-full"
                              placeholder="Last name"
                              style={{ transition: 'all .15s ease' }}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                            <Text
                              borderRadius="md"
                              fontSize="sm"
                              mt="1"
                              textColor="red.500"
                            >
                              {errors.lastName && touched.lastName
                                ? errors.lastName
                                : null}
                            </Text>
                          </div>

                          <div className="relative w-full mb-4">
                            <label
                              className="block uppercase text-gray-700 text-xs font-bold mb-2"
                              htmlFor="email"
                            >
                              Your Email
                            </label>
                            <input
                              id="email"
                              type="email"
                              value={values.email}
                              className="px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm border focus:outline-none focus:shadow-outline w-full"
                              placeholder="Enter your email"
                              style={{ transition: 'all .15s ease' }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <Text
                              borderRadius="md"
                              fontSize="sm"
                              mt="1"
                              textColor="red.500"
                            >
                              {errors.email && touched.email
                                ? errors.email
                                : null}
                            </Text>
                          </div>

                          <div className="relative w-full mb-4">
                            <label
                              className="block uppercase text-gray-700 text-xs font-bold mb-2"
                              htmlFor="email"
                            >
                              Username
                            </label>
                            <input
                              id="username"
                              type="text"
                              value={values.username}
                              className="px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm border focus:outline-none focus:shadow-outline w-full"
                              placeholder="Choose a unique username"
                              style={{ transition: 'all .15s ease' }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <Text
                              borderRadius="md"
                              fontSize="sm"
                              mt="1"
                              textColor="red.500"
                            >
                              {errors.username && touched.username
                                ? errors.username
                                : null}
                            </Text>
                          </div>

                          <div className="mb-3">
                            <div className="relative w-full mb-3">
                              <label
                                id="password"
                                className="block uppercase text-gray-700 text-xs font-bold mb-2"
                                htmlFor="grid-password"
                              >
                                Password
                              </label>
                              <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={values.password}
                                className="px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm border focus:outline-none focus:shadow-outline w-full"
                                placeholder="Choose your password (min 8 chars)"
                                style={{ transition: 'all .15s ease' }}
                                minLength={8}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <span
                                className="cursor-pointer absolute"
                                style={{ bottom: '15px', right: '5px' }}
                                onClick={() => {
                                  setShowPassword(!showPassword);
                                }}
                              >
                                {showPassword ? (
                                  <AiOutlineEye />
                                ) : (
                                  <AiOutlineEyeInvisible />
                                )}
                              </span>
                            </div>
                            <Text
                              borderRadius="md"
                              fontSize="sm"
                              mt="1"
                              textColor="red.500"
                            >
                              {errors.password && touched.password
                                ? errors.password
                                : null}
                            </Text>
                          </div>

                          <div className="text-center mt-6">
                            <Button
                              width={'100%'}
                              rounded={'full'}
                              type="submit"
                              bgColor={'brand.darkBlue'}
                              textColor={'white'}
                              _hover={{ shadow: 'lg' }}
                              fontSize={'sm'}
                              textTransform={'uppercase'}
                              isLoading={isSubmitting}
                              spinner={<BeatLoader size={8} color="white" />}
                              disabled={isSubmitting || !(isValid || dirty)}
                            >
                              Sign Up
                            </Button>
                          </div>
                        </form>
                      )}
                    </Formik>
                    <div className="flex flex-wrap mt-6 mb-6">
                      <div className="w-full text-sm">
                        <span> Already have an account? </span>
                        <a href="/login" className="text-red-800 font-semibold">
                          Login
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Flex>
          </Flex>
        </Flex>
        <FooterSmall />
      </Flex>
    </>
  );
}
